import React, {createContext, useCallback, useContext, useReducer, useMemo} from 'react';
import useSafeDispatch from 'hooks/useSafeDispatch';

const HomeContext = createContext();
HomeContext.displayName = 'HomeContext';

const HomeDispatchContext = createContext();
HomeDispatchContext.displayName = 'HomeDispatchContext';

const actionTypes = {
  fetchData: 'fetchData',
  calcLogoutHeight: 0,
  setCalendarView: 'setCalendarView',
  setCalendarType: 'setCalendarType',
  setResignedEmployeeError: 'setResignedEmployeeError'
};

const initialState = {
  fetch: false,
  logoutHeight: 0,
  calendarView: 'month',
  selectedCalendar: 'user',
  resignedEmployeeError: false
};

function homeReducer(state, action) {
  switch (action.type) {
    case actionTypes.fetchData: {
      return {
        ...state,
        fetch: !state.fetch
      };
    }
    case actionTypes.calcLogoutHeight: {
      return {
        ...state,
        logoutHeight: action.payload
      };
    }
    case actionTypes.setCalendarView: {
      return {
        ...state,
        calendarView: action.payload
      };
    }
    case actionTypes.setCalendarType: {
      return {
        ...state,
        selectedCalendar: action.payload
      };
    }
    case actionTypes.setResignedEmployeeError: {
      return {
        ...state,
        resignedEmployeeError: action.payload
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export {actionTypes, homeReducer};

const HomeProvider = ({children}) => {
  const [state, unsafeDispatch] = useReducer(homeReducer, initialState);

  const memoizedState = useMemo(() => state, [state]);
  const dispatch = useSafeDispatch(unsafeDispatch);

  return (
    <HomeContext.Provider value={memoizedState}>
      <HomeDispatchContext.Provider value={{dispatch}}>{children}</HomeDispatchContext.Provider>
    </HomeContext.Provider>
  );
};

function useHomeState() {
  const context = useContext(HomeContext);
  if (context === undefined) {
    throw new Error('useHomeState must be used within a HomeProvider');
  }

  return context;
}

function useHomeDispatch() {
  const context = useContext(HomeDispatchContext);
  if (context === undefined) {
    throw new Error('useHomeDispatch must be used within a HomeProvider');
  }

  const {dispatch} = context;

  const fetchData = useCallback(() => dispatch({type: actionTypes.fetchData}), [dispatch]);

  const calcLogoutHeight = useCallback(
    (payload) => dispatch({type: actionTypes.calcLogoutHeight, payload}),
    [dispatch]
  );

  const setCalendarView = useCallback(
    (payload) => dispatch({type: actionTypes.setCalendarView, payload}),
    [dispatch]
  );

  const setCalendarType = useCallback(
    (payload) => dispatch({type: actionTypes.setCalendarType, payload}),
    [dispatch]
  );

  const setResignedEmployeeError = useCallback(
    (payload) => dispatch({type: actionTypes.setResignedEmployeeError, payload}),
    [dispatch]
  );

  return {
    dispatch,
    fetchData,
    calcLogoutHeight,
    setCalendarView,
    setCalendarType,
    setResignedEmployeeError
  };
}

export {HomeContext, HomeProvider, useHomeState, useHomeDispatch};
